import DaumPostcodeEmbed from "react-daum-postcode";
import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useLazyQuery, gql } from "@apollo/client";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import CustomMapMarker2 from "../../../../components/common/custom-map-marker-2";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import Header from "../../../../components/common/header";
import {
  GET_SELLER_STORE,
  UPDATE_STORE,
  CREATE_STORE,
  GET_COORDS,
} from "../../api/seller-store";
import BottomSheet from "../../../../components/common/bottom-sheet";
import { track } from "@amplitude/analytics-browser";
import { notifyError } from "../../../../utils/toast";

const SET_SELLER_SIGNUP_SOURCE = gql`
  mutation setSellerSignupSource($signupSource: String!) {
    setSellerSignupSource(signupSource: $signupSource) {
      ok
      error
    }
  }
`;

type Store = {
  id: number;
  img?: string;
  title: string;
  address: string;
  detailAddress?: string;
  navigationInfo?: string;
  contactNumber: string;
  closingHours: string;
  lat: number;
  lng: number;
  signupState: string;
};

const SignupStoreCreation: React.FC = () => {
  const [store, setStore] = useState<Store | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 상태
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const [imgFile, setImgFile] = useState<File | null>(null); // 이미지 파일 상태 추가

  const { loading, error } = useQuery(GET_SELLER_STORE, {
    onCompleted: (data) => {
      setStore(data.getSellerStore);
      setLat(data.getSellerStore.lat);
      setLng(data.getSellerStore.lng);
    },
    fetchPolicy: "network-only",
  });
  const [updateStore] = useMutation(UPDATE_STORE);
  const [createStore] = useMutation(CREATE_STORE);

  const handleSaveStore = async (storeInfo: Store): Promise<void> => {
    if (store) {
      await updateStore({
        variables: {
          id: store.id, // Store ID를 기반으로 업데이트
          title: storeInfo.title,
          address: storeInfo.address,
          detailAddress: storeInfo.detailAddress,
          navigationInfo: storeInfo.navigationInfo,
          contactNumber: storeInfo.contactNumber,
          closingHours: storeInfo.closingHours,
          lat,
          lng,
          img: imgFile, // 이미지 파일 전달
        },
      });
      setStore(storeInfo);
    } else {
      const response = await createStore({
        variables: {
          title: storeInfo.title,
          address: storeInfo.address,
          contactNumber: storeInfo.contactNumber,
          closingHours: storeInfo.closingHours,
          lat,
          lng,
          img: imgFile, // 이미지 파일 전달
        },
      });
      setStore(response.data.createStore);
      window.location.reload();
    }
  };
  const handleOpenModal = () => setIsModalOpen(true); // 모달 열기

  const handleSelectAddress = (lng: number, lat: number) => {
    setLng(lng);
    setLat(lat);
    setIsModalOpen(false); // 모달 닫기
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    track("사장님 입점신청6-1 매장 이미지 추가 클릭");

    if (file) {
      // 20MB 크기 제한 체크 (20MB = 20 * 1024 * 1024 bytes)
      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        notifyError(
          "이미지 크기가 20MB를 초과합니다.\n다른 이미지를 선택해주세요."
        );
        // 파일 input 초기화
        e.target.value = "";
        return;
      }

      setImgFile(file); // 파일을 상태에 저장
    }
  };

  if (loading) return <LoadingSpinnerFull />;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div className="flex flex-col min-h-screen h-full bg-white">
      <Header title="입점 신청" />

      <div className="flex justify-center p-4 h-full">
        <div className="w-full space-y-6 h-full">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">매장 등록</h1>
            <div className="bg-gray-100 rounded-md p-4">
              <p className="text-gray-700">어떤 매장인지 알려주세요.</p>
              <p className="text-gray-700">
                등록하신 정보는 입점 완료 후 수정 가능하니 편하게 입력해주세요.
              </p>
            </div>
          </div>

          <div className="w-full space-y-6">
            {store ? (
              <StoreEditForm
                onSave={handleSaveStore}
                handleOpenModal={handleOpenModal}
                store={store}
                setLng={setLng}
                setLat={setLat}
                lng={lng}
                lat={lat}
                handleImageChange={handleImageChange} // 이미지 파일 핸들러 전달
              />
            ) : null}
            {lat && lng && isModalOpen ? (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white py-6 rounded-xl shadow-lg w-[80%]">
                  <h2 className="text-xl font-medium mb-4">상세 주소 선택</h2>
                  <Map
                    onSelectAddress={handleSelectAddress}
                    lng={lng}
                    lat={lat}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupStoreCreation;

// 매장 수정 폼 컴포넌트
type StoreEditFormProps = {
  onSave: (storeInfo: Store) => Promise<void>;
  store: Store | null;
  handleOpenModal: () => void;
  setLng: (data: number) => void;
  setLat: (data: number) => void;
  lng: number;
  lat: number;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // 이미지 파일 핸들러 추가
};

const StoreEditForm: React.FC<StoreEditFormProps> = ({
  onSave,
  handleOpenModal,
  store,
  setLng,
  setLat,
  lng,
  lat,
  handleImageChange, // 이미지 파일 핸들러 추가
}) => {
  const [title, setTitle] = useState(store?.title || "");
  const [address, setAddress] = useState(store?.address || "");
  const [detailAddress, setDetailAddress] = useState(
    store?.detailAddress || ""
  );
  const [navigationInfo, setNavigationInfo] = useState(
    store?.navigationInfo || ""
  );
  const [contactNumber, setContactNumber] = useState(
    store?.contactNumber || ""
  );
  const [isSpecificSet, setIsSpecificSet] = useState(false);
  const [closingHours, setClosingHours] = useState(store?.closingHours || "");
  const [getCoordsFromAddress] = useLazyQuery(GET_COORDS);
  const [isSignupSourceModalOpen, setIsSignupSourceModalOpen] = useState(false);
  const [signupSource, setSignupSource] = useState("");
  const [otherSource, setOtherSource] = useState("");
  const [referralName, setReferralName] = useState("");
  const signupSources = [
    "방문 영업",
    "인스타그램",
    "스레드",
    "지인추천",
    "기타",
  ];
  const [setSellerSignupSource] = useMutation(SET_SELLER_SIGNUP_SOURCE);

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const handleSubmit = () => {
    if (
      !title ||
      !address ||
      selectedHour.length <= 1 ||
      selectedMinute.length <= 1 ||
      !lat ||
      !lng
    ) {
      setErrorMessage("필수 항목들을 입력해주세요. (*표시 항목)");
      return;
    }

    setIsSignupSourceModalOpen(true);
  };

  const handleSignupSourceModalClose = async () => {
    setIsSaving(true);
    setErrorMessage(null);

    try {
      await onSave({
        id: store?.id || 0,
        title,
        address,
        detailAddress,
        navigationInfo,
        contactNumber,
        closingHours,
        lng,
        lat,
        signupState: store?.signupState || "",
      });

      const signupSourceResult = await setSellerSignupSource({
        variables: {
          signupSource:
            signupSource === "기타"
              ? otherSource
              : signupSource === "지인추천"
                ? `지인추천: ${referralName}`
                : signupSource,
        },
      });

      if (!signupSourceResult.data?.setSellerSignupSource?.ok) {
        throw new Error(
          signupSourceResult.data?.setSellerSignupSource?.error ||
            "가입 경로 저장에 실패했습니다.",
        );
      }

      setIsSignupSourceModalOpen(false);
      track("사장님 입점신청6 매장 등록 완료");
      navigate("/boss/signup/onboarding/reserve-pack");
    } catch (error) {
      notifyError(
        error instanceof Error
          ? error.message
          : "매장 정보 저장에 실패했습니다. 다시 시도해주세요.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddressComplete = (data: any) => {
    const fullAddress = data.address;

    setAddress(fullAddress);
    setOpenPostModal(false); // 주소 선택 완료시 모달 닫기 추가

    getCoordsFromAddress({
      variables: {
        address: fullAddress,
      },
    }).then((response) => {
      setLng(response.data.getCoords.lng);
      setLat(response.data.getCoords.lat);
    });
  };

  const [openPostModal, setOpenPostModal] = useState(false);

  const handleAddressClick = () => {
    setAddress("");
    setLng(0);
    setLat(0);
    setOpenPostModal(true);
  };

  const handleSpecificAddressClick = () => {
    handleOpenModal();
    setIsSpecificSet(true);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(
    store?.closingHours?.split(":")[0] || ""
  );
  const [selectedMinute, setSelectedMinute] = useState(
    store?.closingHours?.split(":")[1] || ""
  );
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);

  const handleSaveTime = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isModalOpen && hourScrollRef.current && minuteScrollRef.current) {
      const hourIndex = parseInt(selectedHour);
      const minuteIndex = Math.floor(parseInt(selectedMinute) / 5);

      const itemHeight = 40; // py-2 텍스트의 높이 (8px * 2) + text-lg (32px) = 48px

      hourScrollRef.current.scrollTop = hourIndex * itemHeight;
      minuteScrollRef.current.scrollTop = minuteIndex * itemHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  useEffect(() => {
    setClosingHours(`${selectedHour}:${selectedMinute}`);
  }, [selectedHour, selectedMinute]);

  const handleModalClose = () => {
    // 시간 분 합치기
    setClosingHours(`${selectedHour}:${selectedMinute}`);
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6 h-full">
      <div className="space-y-2">
        <label className="text-black font-medium text-xl">매장 이름*</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition text-gray-700"
          placeholder="매장 이름을 입력해주세요"
        />
      </div>

      <div className="space-y-2">
        <label className="text-black font-medium text-xl">매장 주소*</label>
        <div className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition text-gray-700">
          {address ? (
            <div className="flex justify-between items-center">
              {address}
              <div
                onClick={handleAddressClick}
                className="text-gray-700 border border-gray-300 rounded py-0.5 px-2 bg-gray-100"
              >
                주소 변경하기
              </div>
            </div>
          ) : (
            <span onClick={handleAddressClick} className="text-primary">
              주소를 선택해주세요
            </span>
          )}
        </div>

        {openPostModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-xl w-full max-w-xl">
              <DaumPostcodeEmbed
                onComplete={handleAddressComplete}
                scriptUrl="//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
                className="w-full h-96"
              />
              <button
                onClick={() => setOpenPostModal(false)}
                className="w-full mt-4 py-2 bg-gray-200 rounded-lg"
              >
                닫기
              </button>
            </div>
          </div>
        )}

        <input
          value={detailAddress}
          onChange={(e) => setDetailAddress(e.target.value)}
          placeholder="상세 주소를 입력해주세요"
          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition text-gray-700"
        />
      </div>

      <div className="space-y-2">
        <label className="text-black font-medium text-xl">
          찾아오는 길 안내 (선택)
        </label>
        <div className="bg-gray-100 rounded-md p-4">
          <div className="text-gray-700">
            손님이 매장을 찾기 어려울 경우 참고할 수 있는 메시지를 적어주세요.
          </div>
          <p className="text-gray-700">
            예시) 00 오른쪽으로 가서 주차장 옆에 있습니다.
          </p>
        </div>
        <input
          value={navigationInfo}
          onChange={(e) => setNavigationInfo(e.target.value)}
          placeholder="찾아오는 길을 적어주세요"
          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition text-gray-700"
        />
      </div>

      <div className="flex flex-col space-y-2">
        <label className="text-black font-medium text-xl">
          지도에서 한 번 더 설정*
        </label>
        <div className="text-gray-700">
          지도를 움직여 정확한 위치를 선택해주세요.
        </div>
        <div className="text-primary w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition">
          {isSpecificSet ? (
            <div className="flex justify-between items-center">
              <div className="text-green-500">완료</div>
              <div
                onClick={handleSpecificAddressClick}
                className="text-gray-700 border border-gray-300 rounded py-0.5 px-2 bg-gray-100"
              >
                다시 설정하기
              </div>
            </div>
          ) : (
            <div onClick={handleSpecificAddressClick}>설정하기</div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-black font-medium text-xl">
          영업 종료 시간*
        </label>
        <div className="flex">
          <button
            onClick={() => {
              setSelectedHour("18");
              setSelectedMinute("00");
              setIsModalOpen(true);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition text-gray-700"
          >
            {selectedHour && selectedMinute ? (
              <div className="w-full flex justify-between items-center">
                <div>
                  {selectedHour}:{selectedMinute}
                </div>
                <div className="text-gray-700 border border-gray-300 rounded py-0.5 px-2 bg-gray-100">
                  변경하기
                </div>
              </div>
            ) : (
              <div className="w-full flex text-primary">
                영업 종료 시간을 선택해주세요
              </div>
            )}
          </button>
        </div>
        <BottomSheet
          isOpen={isModalOpen}
          mainText="영업 종료 시간 선택"
          onClose={handleModalClose}
          onButtonClick={handleSaveTime}
          buttonText="저장하기"
          disableDrag={true}
        >
          <div className="flex justify-center items-center py-4 space-x-6">
            <div
              ref={hourScrollRef}
              className="overflow-y-auto max-h-60 w-24 text-center"
            >
              {Array.from({ length: 24 }, (_, hour) => {
                const hourStr = String(hour).padStart(2, "0");
                return (
                  <button
                    key={hourStr}
                    className={`w-full py-2 text-lg ${
                      selectedHour === hourStr
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100"
                    }`}
                    onClick={(e) => {
                      setSelectedHour(hourStr);
                    }}
                  >
                    {hourStr}
                  </button>
                );
              })}
            </div>
            <div className="text-lg font-bold">:</div>
            {/* 분 선택 */}
            <div
              ref={minuteScrollRef}
              className="overflow-y-auto max-h-60 w-24 text-center"
            >
              {Array.from({ length: 12 }, (_, i) => {
                const minute = String(i * 5).padStart(2, "0");
                return (
                  <button
                    key={minute}
                    className={`w-full py-2 text-lg ${
                      selectedMinute === minute
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100"
                    }`}
                    onClick={(e) => {
                      setSelectedMinute(minute);
                    }}
                  >
                    {minute}
                  </button>
                );
              })}
            </div>
          </div>
        </BottomSheet>
      </div>

      <div className="space-y-2">
        <label className="text-black font-medium text-xl">
          매장 연락처 (선택)
        </label>
        <div className="bg-gray-100 rounded-md p-4">
          <p className="text-gray-700">손님에게 보여지는 매장 연락처에요.</p>
        </div>
        <input
          value={contactNumber}
          onChange={(e) => setContactNumber(e.target.value)}
          placeholder="매장 연락처를 입력해주세요"
          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition text-gray-700  "
        />
      </div>

      <div className="space-y-2">
        <label className="text-black font-medium text-xl">
          매장 대표 사진 (메뉴 사진)
        </label>
        <p className="text-gray-700">
          손님들이 제일 먼저 보는 화면이에요.{" "}
          <span className="font-semibold text-orange-400">대표 메뉴 사진</span>
          으로 가게 매력을 보여주세요!
        </p>
        <p className="text-gray-700"></p>
        <div className="bg-gray-100 rounded-md p-4">
          <p className="text-green-600 italic font-medium">
            어떤 사진으로 선택해야 할지 고민되시면 마감히어로가 사장님께 연락을
            드리고 가장 예쁜 사진으로 변경해드릴게요! 편하게 골라주세요.
          </p>
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange} // 이미지 파일 변경 핸들러
          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-primary transition text-gray-700"
        />
      </div>

      {errorMessage && (
        <div className="text-red-500 text-sm mb-4">{errorMessage}</div>
      )}

      {isSignupSourceModalOpen && (
        <BottomSheet
          isOpen={isSignupSourceModalOpen}
          mainText="가입 경로 선택"
          onClose={() => setIsSignupSourceModalOpen(false)}
          onButtonClick={() => {
            if (
              !isSaving &&
              signupSource &&
              (signupSource !== "기타" || otherSource) &&
              (signupSource !== "지인추천" || referralName)
            ) {
              void handleSignupSourceModalClose();
            }
          }}
          buttonText={isSaving ? "저장 중..." : "다음"}
          disableDrag={true}
        >
          <div className="p-4 space-y-4">
            <p className="text-gray-700">마감히어로를 어떻게 알게 되셨나요?</p>
            <div className="space-y-2">
              {signupSources.map((source) => (
                <div key={source}>
                  <button
                    onClick={() => {
                      setSignupSource(source);
                      if (source !== "기타" && source !== "지인추천") {
                        setOtherSource("");
                        setReferralName("");
                      }
                    }}
                    className={clsx(
                      "w-full py-3 px-4 rounded-xl border text-left",
                      signupSource === source
                        ? "border-primary text-primary bg-primary/5"
                        : "border-gray-300 text-gray-700"
                    )}
                  >
                    {source}
                  </button>
                  {signupSource === "지인추천" && source === "지인추천" && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="text-blue-700 text-sm font-medium mb-2">
                        💡 추천인 정보 입력
                      </div>
                      <input
                        value={referralName}
                        onChange={(e) => setReferralName(e.target.value)}
                        placeholder="추천해주신 분은(매장명) 누구인가요?"
                        className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-gray-700 bg-white"
                      />
                    </div>
                  )}
                  {signupSource === "기타" && source === "기타" && (
                    <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-xl">
                      <div className="text-orange-700 text-sm font-medium mb-2">
                        💡 추가 정보 입력
                      </div>
                      <input
                        value={otherSource}
                        onChange={(e) => setOtherSource(e.target.value)}
                        placeholder="가입 경로를 입력해주세요"
                        className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition text-gray-700 bg-white"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </BottomSheet>
      )}

      <button
        onClick={handleSubmit}
        disabled={isSaving}
        className="w-full py-3 bg-primary text-white font-semibold rounded-xl transition duration-150 hover:brightness-90 active:brightness-90 disabled:opacity-50"
      >
        {isSaving ? "저장 중..." : "다음"}
      </button>
    </div>
  );
};

const Map = ({
  onSelectAddress,
  lat,
  lng,
}: {
  onSelectAddress: (lat: number, lng: number) => void;
  lng: number;
  lat: number;
}) => {
  const mapElement = useRef<HTMLDivElement | null>(null);

  const { naver } = window;
  let map: naver.maps.Map;
  // 지도가 이동하였을 때 가려진 부분의 마커는 숨기고
  // 노출된 부분의 마커를 표시하는 작업을 하기 위해 map 인스턴스를 state로 관리
  const [newMap, setNewMap] = useState<naver.maps.Map | null>(null);

  const center = new naver.maps.LatLng(lat, lng);
  // [지도를 그리는 effect]
  useEffect(() => {
    if (!mapElement.current || !naver) {
      return;
    }

    const mapOptions: naver.maps.MapOptions = {
      center,
      zoom: 15,
      minZoom: 7,
      maxZoom: 19,
      zoomControl: true,
      zoomControlOptions: {
        style: naver.maps.ZoomControlStyle.SMALL,
        position: naver.maps.Position.TOP_RIGHT,
      },
      mapDataControl: false,
      scaleControl: false,
    };

    //설정해놓은 옵션을 바탕으로 지도 생성
    // eslint-disable-next-line react-hooks/exhaustive-deps
    map = new naver.maps.Map(mapElement.current, mapOptions);
    setNewMap(map);
    const marker = new naver.maps.Marker({
      position: center,
      map,
      clickable: true,
      // [마커 커스터마이징]
      icon: {
        //html element를 반환하는 CustomMapMarker 컴포넌트 할당
        content: CustomMapMarker2(),
        //마커의 기준위치 지정
        anchor: new naver.maps.Point(25, 58),
      },
    });

    // [마커는 항상 맵 중앙에]
    naver.maps.Event.addListener(map, "drag", function (e) {
      const centerCoord = map.getCenter();
      marker.setPosition(centerCoord);
    });
  }, []);

  function onClickClose() {
    const center = newMap?.getCenter();

    const lat = center?.y as number;
    const lng = center?.x as number;
    onSelectAddress(lng, lat);
  }
  return (
    <div className="w-[100%] h-[400px]">
      <div
        ref={mapElement}
        id="map"
        style={{ width: "100%", height: 420 }}
      ></div>
      <button
        onClick={() => onClickClose()}
        className="mt-4 px-4 py-2 bg-gray-400 text-white rounded"
      >
        완료
      </button>
    </div>
  );
};
